import { Card, Icon, NonIdealState, ProgressBar } from '@blueprintjs/core';
import { useAppStateContext } from 'Client';
import AuthorizedRoute from 'Components/AuthorizedRoute';
import AppContentView from 'Components/Generic/AppContentView';
import BOSNavbar from 'Components/Generic/Navigation/BOSNavbar';
import BOSSideTabNav from 'Components/Generic/Navigation/BOSSideTabNav';
import DevLoginPage from 'Components/Login/DevLoginPage';
import LoginPage from 'Components/Login/LoginPage';
import LogoutPage from 'Components/Login/LogoutPage';
import SiteDialogContainer from 'Components/SiteDialogContainer';
import SiteDrawerContainer from 'Components/SiteDrawerContainer';
import { SITE_PAGES } from 'Config';
import { useAuthContext } from 'Data/context/Auth';
import Accounts from 'Pages/Accounts';
import Admin from 'Pages/Admin';
import UserDetailsPage from 'Pages/Admin/Users/UserDetailsPage';
import Configuration from 'Pages/Configuration';
import Dashboard from 'Pages/Dashboard';
import Help from 'Pages/Help';
import Requests from 'Pages/Requests';
import ServiceContracts from 'Pages/ServiceContracts';
import Transactions from 'Pages/Transactions';
import PropTypes from 'prop-types';
import {
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useNavigation,
} from 'react-router';
import Home from './Home';
import styles from './routes.module.css';

const Layout = ({ isLoggedIn, userName, loading }) => {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);

  return (
    <>
      <div
        style={{
          display: 'flex',
        }}>
        {isLoggedIn && <BOSSideTabNav />}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            height: '100vh',
            overflow: 'hidden',
          }}>
          <BOSNavbar
            isLoggedIn={isLoggedIn}
            userName={userName}
            showHome={!isLoggedIn}
          />
          <div className={styles.content_background}>
            {loading || isNavigating ? <ProgressBar /> : <Outlet />}
          </div>
        </div>
      </div>
      <SiteDialogContainer />
      <SiteDrawerContainer />
    </>
  );
};

Layout.propTypes = {
  isLoggedIn: PropTypes.bool,
  userName: PropTypes.string,
  loading: PropTypes.bool,
};

const SiteRoutes = () => {
  const { isLoggedIn, userName } = useAuthContext();
  const { loading } = useAppStateContext();

  return (
    <Routes>
      <Route
        path=''
        element={
          <Layout
            isLoggedIn={isLoggedIn}
            userName={userName}
            loading={loading}
          />
        }>
        <Route
          path={SITE_PAGES.LOG_IN.to}
          element={
            isLoggedIn ? (
              <Navigate to={SITE_PAGES.HOME.to} />
            ) : process.env.REACT_APP_LOCAL ? (
              <DevLoginPage />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path={SITE_PAGES.LOG_OUT.to} element={<LogoutPage />} />
        <Route index element={<Home />} />
        <Route path={SITE_PAGES.HELP.to} element={<Help />} />
        <Route
          path={SITE_PAGES.ACCESS_DENIED.to}
          element={
            <AppContentView hideBreadcrumbs title='Access Denied'>
              {!isLoggedIn && <Card>Please log in.</Card>}
              {isLoggedIn && (
                <Card>
                  <NonIdealState
                    className={styles.error_state}
                    icon={'warning-sign'}>
                    <span style={{ fontSize: 'large' }}>
                      You do not have sufficient permissions to view this page.
                    </span>
                  </NonIdealState>
                </Card>
              )}
            </AppContentView>
          }
        />
        <Route
          path='/*'
          element={
            <AppContentView hideBreadcrumbs title='Page not found'>
              <Card>
                <NonIdealState
                  icon={'search'}
                  title={'Uh, oh!'}
                  description={
                    'No page exists for this URL. If you think this is an error, contact the developers.'
                  }
                  action={
                    !isLoggedIn && (
                      <NavLink
                        className={`bp5-button ${styles.nav_button}`}
                        to={SITE_PAGES.LOG_IN.to}
                        onMouseDown={(e) => e.preventDefault()}>
                        <Icon icon={'open-application'} size={24} />
                        <span>Go to login page</span>
                      </NavLink>
                    )
                  }
                />
              </Card>
            </AppContentView>
          }
        />
        {/* Protected routes */}
        <Route
          path={SITE_PAGES.DASHBOARD.to}
          element={
            <AuthorizedRoute requiredAuth={SITE_PAGES.DASHBOARD.requiredAuth}>
              <Dashboard />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${SITE_PAGES.ORDER_LANDING.to}/*`}
          element={
            <AuthorizedRoute
              requiredAuth={SITE_PAGES.ORDER_LANDING.requiredAuth}>
              <Requests />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${SITE_PAGES.TRANSACTION_LANDING.to}/*`}
          element={
            <AuthorizedRoute
              requiredAuth={SITE_PAGES.TRANSACTION_LANDING.requiredAuth}>
              <Transactions />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${SITE_PAGES.ACCOUNT_LANDING.to}/*`}
          element={
            <AuthorizedRoute
              requiredAuth={SITE_PAGES.ACCOUNT_LANDING.requiredAuth}>
              <Accounts />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${SITE_PAGES.ADMIN_LANDING.to}/*`}
          element={
            <AuthorizedRoute
              requiredAuth={SITE_PAGES.ADMIN_LANDING.requiredAuth}>
              <Admin />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${SITE_PAGES.SERVICE_CONTRACT_LANDING.to}/*`}
          element={
            <AuthorizedRoute
              requiredAuth={SITE_PAGES.SERVICE_CONTRACT_LANDING.requiredAuth}>
              <ServiceContracts />
            </AuthorizedRoute>
          }
        />
        <Route
          path={SITE_PAGES.CONFIG.to}
          element={
            <AuthorizedRoute requiredAuth={SITE_PAGES.CONFIG.requiredAuth}>
              <Configuration />
            </AuthorizedRoute>
          }
        />
        <Route
          path={SITE_PAGES.MY_ACCOUNT.to}
          element={
            <AuthorizedRoute requiredAuth={SITE_PAGES.MY_ACCOUNT.requiredAuth}>
              <UserDetailsPage isMyAccountPage />
            </AuthorizedRoute>
          }
        />
      </Route>
    </Routes>
  );
};

SiteRoutes.propTypes = {
  loading: PropTypes.bool,
};

export default SiteRoutes;
